/**
 * Results Content Component (Client-side)
 * Handles the actual data fetching and display of results
 */

'use client';

import { useEffect, useState } from 'react';
import { useSearchParams, useRouter } from 'next/navigation';
import Link from 'next/link';
import SearchBar from '@/components/SearchBar';
import ResultsCard from '@/components/ResultsCard';
import AdsBlock from '@/components/AdsBlock';
import ExtensionBanner from '@/components/ExtensionBanner';
import { VertexResponse, OpenGraphData } from '@/lib/validators';
import { trackSearch, trackResultView } from '@/lib/analytics';
import { functions, httpsCallable } from '@/lib/firebase.client';
import { useAuth } from '@/lib/auth-context';

export default function ResultsContent() {
  const searchParams = useSearchParams();
  const router = useRouter();
  const { user } = useAuth();
  const query = searchParams.get('q') || '';
  const mode = (searchParams.get('mode') || 'FSA') as 'FSA' | 'HSA';

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isRateLimitError, setIsRateLimitError] = useState(false);
  const [showSignupCta, setShowSignupCta] = useState(false);
  const [result, setResult] = useState<VertexResponse | null>(null);
  const [relatedSearches, setRelatedSearches] = useState<string[]>([]);
  const [ogData, setOgData] = useState<OpenGraphData | null>(null);
  const [ogLoading, setOgLoading] = useState(false);

  // Check if query is a URL
  const isURL = (str: string) => {
    // Remove url: prefix if present
    const cleanStr = str.startsWith('url:') ? str.slice(4) : str;
    try {
      new URL(cleanStr);
      return true;
    } catch {
      return /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/.test(cleanStr);
    }
  };

  // Extract URL from query
  const extractUrl = (q: string) => {
    const cleanQ = q.startsWith('url:') ? q.slice(4) : q;
    // Add https:// if no protocol
    if (!cleanQ.startsWith('http://') && !cleanQ.startsWith('https://')) {
      return `https://${cleanQ}`;
    }
    return cleanQ;
  };

  useEffect(() => {
    if (!query) {
      router.push('/');
      return;
    }

    const fetchResults = async () => {
      setLoading(true);
      setError(null);

      try {
        trackSearch(query, mode);

        // Call the Cloud Function directly
        const checkEligibility = httpsCallable(functions, 'checkEligibility');
        const response = await checkEligibility({ q: query, mode });
        
        const data = response.data as VertexResponse;
        setResult(data);

        // Track result view
        if (data.classification && data.confidence) {
          trackResultView(query, data.classification, data.confidence);
        }

        // Fetch related searches
        fetchRelatedSearches(query);

        // Fetch OG data if query is a URL
        if (isURL(query)) {
          fetchOgData(extractUrl(query));
        }
      } catch (err: any) {
        console.error('Error fetching results:', err);
        
        // Check if it's a rate limit error
        const errorCode = err?.code || '';
        const errorMessage = err?.message || '';
        
        if (errorCode === 'functions/resource-exhausted' || 
            errorMessage.includes('Rate limit exceeded')) {
          setIsRateLimitError(true);
          // Show signup CTA for anonymous users
          if (!user) {
            setShowSignupCta(true);
            setError('You\'ve reached the search limit. Sign up for free to get more searches!');
          } else {
            setError('Rate limit exceeded. Please try again later.');
          }
        } else {
          setError(errorMessage || 'An error occurred');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchResults();
  }, [query, mode, router]);

  const fetchOgData = async (url: string) => {
    setOgLoading(true);
    try {
      const scrapeOpenGraph = httpsCallable(functions, 'scrapeOpenGraph');
      const response = await scrapeOpenGraph({ url });
      const data = response.data as { success: boolean; data: OpenGraphData };
      if (data.success && data.data) {
        setOgData(data.data);
      }
    } catch (error) {
      console.error('Error fetching OG data:', error);
    } finally {
      setOgLoading(false);
    }
  };

  const fetchRelatedSearches = async (q: string) => {
    try {
      const relatedSearchesCallable = httpsCallable(functions, 'relatedSearches');
      const response = await relatedSearchesCallable({ q });
      const data = response.data as { related: string[] };
      setRelatedSearches(data.related || []);
    } catch (error) {
      console.error('Error fetching related searches:', error);
    }
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-8">
          <div className="inline-block animate-spin rounded-full h-16 w-16 border-b-4 border-primary-500 mb-4"></div>
          <p className="text-gray-600 text-lg">Analyzing eligibility...</p>
        </div>
      </div>
    );
  }

  if (error) {
    // Special UI for rate limit with signup CTA
    if (isRateLimitError && showSignupCta) {
      return (
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="bg-gradient-to-br from-primary-50 to-blue-50 border-2 border-primary-200 rounded-2xl p-8 text-center">
            <div className="text-6xl mb-4">🔒</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Search Limit Reached
            </h2>
            <p className="text-gray-700 mb-6 max-w-md mx-auto">
              You&apos;ve used all your free searches. Create a free account to unlock 
              <strong className="text-primary-600"> 100 searches per hour</strong> and save your favorites!
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <Link
                href="/auth/signup"
                className="bg-primary-500 text-white px-8 py-4 rounded-xl font-bold text-lg hover:bg-primary-600 transition-all transform hover:scale-105 shadow-lg"
              >
                🚀 Sign Up Free
              </Link>
              <Link
                href="/auth/signin"
                className="bg-white text-primary-600 px-8 py-4 rounded-xl font-semibold text-lg border-2 border-primary-300 hover:bg-primary-50 transition-colors"
              >
                Already have an account? Sign In
              </Link>
            </div>

            <div className="bg-white/60 rounded-xl p-4 max-w-sm mx-auto">
              <p className="text-sm text-gray-600 mb-2">
                <strong>Free account benefits:</strong>
              </p>
              <ul className="text-sm text-gray-600 text-left space-y-1">
                <li>✓ 100 searches per hour (vs 10 for guests)</li>
                <li>✓ Save favorite items</li>
                <li>✓ Search history</li>
                <li>✓ Submit new items</li>
              </ul>
            </div>

            <button
              onClick={() => router.push('/')}
              className="mt-6 text-gray-500 hover:text-gray-700 text-sm underline"
            >
              Return to Home
            </button>
          </div>
        </div>
      );
    }

    // Standard error UI
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="bg-red-50 border-2 border-red-200 rounded-2xl p-8 text-center">
          <div className="text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-red-900 mb-4">
            {isRateLimitError ? 'Rate Limit Exceeded' : 'Error'}
          </h2>
          <p className="text-red-700 mb-6">{error}</p>
          <button
            onClick={() => router.push('/')}
            className="bg-primary-500 text-white px-6 py-3 rounded-xl font-semibold hover:bg-primary-600 transition-colors"
          >
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  if (!result) {
    return null;
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Search Bar */}
      <div className="mb-8">
        <SearchBar initialQuery={query} initialMode={mode} />
      </div>

      {/* AdSense - Results Top */}
      <div className="mb-8">
        <AdsBlock slot="results_top" format="horizontal" />
      </div>

      {/* Results Card */}
      <div className="mb-8">
        <ResultsCard
          query={query}
          classification={result.classification}
          requires={result.requires}
          confidence={result.confidence}
          rationale={result.rationale}
          sources={result.sources}
          notes={result.notes}
          ogData={ogData}
          ogLoading={ogLoading}
          isUrl={isURL(query)}
        />
      </div>

      {/* Low Confidence - Report Button */}
      {result.confidence < 0.7 && result.confidence >= 0.5 && (
        <div className="mb-8 bg-amber-50 border border-amber-200 rounded-xl p-6 text-center">
          <p className="text-amber-900 mb-4">
            This result has medium confidence. If you know this information is incorrect, please
            help us improve.
          </p>
          <button
            onClick={() => router.push(`/submit?prefill=${encodeURIComponent(query)}`)}
            className="bg-amber-600 text-white px-6 py-3 rounded-xl font-semibold hover:bg-amber-700 transition-colors"
          >
            Report Issue / Submit Correction
          </button>
        </div>
      )}

      {/* Related Searches */}
      {relatedSearches.length > 0 && (
        <div className="mb-8 bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4">People Also Searched For</h3>
          <div className="flex flex-wrap gap-2">
            {relatedSearches.map((term) => (
              <a
                key={term}
                href={`/results?q=${encodeURIComponent(term)}&mode=${mode}`}
                className="px-4 py-2 bg-primary-50 border border-primary-200 rounded-full text-primary-700 hover:bg-primary-100 transition-colors font-medium text-sm"
              >
                {term}
              </a>
            ))}
          </div>
        </div>
      )}

      {/* AdSense - Results Bottom */}
      <div className="mb-8">
        <AdsBlock slot="results_bottom" format="horizontal" />
      </div>

      {/* Chrome Extension Banner */}
      <div className="mb-8">
        <ExtensionBanner variant="inline" />
      </div>

      {/* CTA - Submit Item */}
      <div className="bg-gradient-to-r from-primary-50 to-primary-100 rounded-2xl shadow-lg p-8 text-center">
        <h3 className="text-2xl font-bold text-gray-900 mb-3">
          Know of an eligible item we should add?
        </h3>
        <p className="text-gray-700 mb-6">
          Help the community by submitting FSA/HSA eligible items with supporting documentation.
        </p>
        <a
          href="/submit"
          className="inline-block bg-primary-500 text-white px-8 py-3 rounded-xl font-semibold hover:bg-primary-600 transition-colors"
        >
          Submit an Item →
        </a>
      </div>
    </div>
  );
}

